import { useCallback, useEffect, useRef, useState } from "react";
import { notificationsAPI, type NotificationCounts, type NotificationItem } from "@/api/notifications";
import { usePolling } from "@/shared/hooks/usePolling";

const TOAST_AUTO_DISMISS_MS = 12_000;

function sortNewestFirst(items: NotificationItem[]): NotificationItem[] {
	return [...items].sort((a, b) => {
		const aTime = a.createdAt || "";
		const bTime = b.createdAt || "";
		if (aTime !== bTime) return bTime.localeCompare(aTime);
		return (b.id || "").localeCompare(a.id || "");
	});
}

function unreadFieldForTab(tab: NotificationItem["tab"]): keyof NotificationCounts {
	if (tab === "creator") return "creatorUnread";
	if (tab === "campaign") return "campaignUnread";
	return "financeUnread";
}

function decrementUnread(counts: NotificationCounts, tab: NotificationItem["tab"]): NotificationCounts {
	const field = unreadFieldForTab(tab);
	return {
		...counts,
		totalUnread: Math.max(0, counts.totalUnread - 1),
		[field]: Math.max(0, counts[field] - 1),
	};
}

const emptyCounts: NotificationCounts = {
	totalUnread: 0,
	creatorUnread: 0,
	campaignUnread: 0,
	financeUnread: 0,
};

export function useNotifications(enabled: boolean) {
	const [counts, setCounts] = useState<NotificationCounts>(emptyCounts);
	const [items, setItems] = useState<NotificationItem[]>([]);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [loadingList, setLoadingList] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [toastItem, setToastItem] = useState<NotificationItem | null>(null);
	const panelOpenRef = useRef(false);
	const lastUnreadRef = useRef<number | null>(null);
	const seenToastIdsRef = useRef<Set<string>>(new Set());
	const toastTimerRef = useRef<number | null>(null);
	const itemsRef = useRef<NotificationItem[]>(items);
	itemsRef.current = items;

	const clearToastTimer = useCallback(() => {
		if (toastTimerRef.current != null) {
			window.clearTimeout(toastTimerRef.current);
			toastTimerRef.current = null;
		}
	}, []);

	const dismissToast = useCallback(() => {
		clearToastTimer();
		setToastItem(null);
	}, [clearToastTimer]);

	const showToast = useCallback(
		(item: NotificationItem) => {
			if (seenToastIdsRef.current.has(item.id)) return;
			seenToastIdsRef.current.add(item.id);
			setToastItem(item);
			clearToastTimer();
			toastTimerRef.current = window.setTimeout(() => {
				setToastItem(null);
				toastTimerRef.current = null;
			}, TOAST_AUTO_DISMISS_MS);
		},
		[clearToastTimer],
	);

	// Rejects when the counts request fails so the poller can see a 401 and stop.
	const loadCounts = useCallback(async () => {
		if (!enabled) return;
		const next = await notificationsAPI.getCounts();
		const previousUnread = lastUnreadRef.current;
		setCounts(next);

		if (previousUnread === null) {
			lastUnreadRef.current = next.totalUnread;
			try {
				const page = await notificationsAPI.list("all", null, 20);
				for (const item of page.items) {
					if (!item.readAt) seenToastIdsRef.current.add(item.id);
				}
			} catch (err) {
				console.error("Failed to seed notification toast ids", err);
			}
			return;
		}

		const increased = next.totalUnread > previousUnread;
		lastUnreadRef.current = next.totalUnread;

		if (!increased || panelOpenRef.current) return;

		try {
			const page = await notificationsAPI.list("all", null, 20);
			const newestUnread = sortNewestFirst(page.items).find(
				(item) => !item.readAt && !seenToastIdsRef.current.has(item.id),
			);
			if (newestUnread) showToast(newestUnread);
		} catch (err) {
			console.error("Failed to load notification for toast", err);
		}
	}, [enabled, showToast]);

	const refreshCounts = useCallback(async () => {
		try {
			await loadCounts();
		} catch (err) {
			console.error("Failed to load notification counts", err);
		}
	}, [loadCounts]);

	const loadList = useCallback(async () => {
		if (!enabled) return;
		setLoadingList(true);
		setError(null);
		try {
			const page = await notificationsAPI.list("all");
			setItems(sortNewestFirst(page.items));
			setNextCursor(page.nextCursor);
		} catch (err: any) {
			console.error("Failed to load notifications", err);
			setError(err?.response?.data?.message || "Failed to load notifications");
		} finally {
			setLoadingList(false);
		}
	}, [enabled]);

	const loadMore = useCallback(async () => {
		if (!enabled || !nextCursor || loadingMore) return;
		setLoadingMore(true);
		try {
			const page = await notificationsAPI.list("all", nextCursor);
			setItems((prev) => sortNewestFirst([...prev, ...page.items]));
			setNextCursor(page.nextCursor);
		} catch (err) {
			console.error("Failed to load more notifications", err);
		} finally {
			setLoadingMore(false);
		}
	}, [enabled, loadingMore, nextCursor]);

	const markRead = useCallback(
		async (notificationId: string) => {
			const snapshot = itemsRef.current;
			const target = snapshot.find((item) => item.id === notificationId);
			if (target && !target.readAt) {
				const readAt = new Date().toISOString();
				itemsRef.current = snapshot.map((item) => (item.id === notificationId ? { ...item, readAt } : item));
				setItems(itemsRef.current);
				setCounts((prev) => decrementUnread(prev, target.tab));
			}
			if (toastItem?.id === notificationId) dismissToast();
			try {
				await notificationsAPI.markRead(notificationId);
				await refreshCounts();
			} catch (err) {
				console.error("Failed to mark notification read", err);
				await loadList();
				await refreshCounts();
			}
		},
		[dismissToast, loadList, refreshCounts, toastItem?.id],
	);

	const markAllRead = useCallback(async () => {
		try {
			let cursor: string | null | undefined = undefined;
			let guard = 0;
			do {
				const result = await notificationsAPI.markAllRead("all", cursor);
				cursor = result.nextCursor;
				guard += 1;
			} while (cursor && guard < 40);
			setItems((prev) =>
				prev.map((item) => (item.readAt ? item : { ...item, readAt: new Date().toISOString() })),
			);
			dismissToast();
			await Promise.all([loadList(), refreshCounts()]);
		} catch (err) {
			console.error("Failed to mark all notifications read", err);
			await Promise.all([loadList(), refreshCounts()]);
		}
	}, [dismissToast, loadList, refreshCounts]);

	const onPanelOpen = useCallback(async () => {
		panelOpenRef.current = true;
		dismissToast();
		await Promise.all([refreshCounts(), loadList()]);
	}, [dismissToast, loadList, refreshCounts]);

	const onPanelClose = useCallback(() => {
		panelOpenRef.current = false;
	}, []);

	// 30 s while visible, 5 min while hidden, one fetch on return, stop on 401/403.
	usePolling(loadCounts, { enabled });

	useEffect(() => clearToastTimer, [clearToastTimer]);

	return {
		counts,
		items,
		nextCursor,
		loadingList,
		loadingMore,
		error,
		toastItem,
		loadMore,
		markRead,
		markAllRead,
		dismissToast,
		onPanelOpen,
		onPanelClose,
		refreshCounts,
	};
}
